import { inject, Injectable } from '@angular/core';
import { SurveyDatabase, SurveySnapshot, SurveySnapshotReason } from '../db/survey.db';
import { Survey } from '../models/survey.model';
import { countSurvey, SurveyLogService } from './survey-log.service';

export const MAX_SNAPSHOTS_PER_SURVEY = 3;

@Injectable({ providedIn: 'root' })
export class SurveySnapshotService {
  private readonly db = inject(SurveyDatabase);
  private readonly surveyLog = inject(SurveyLogService);

  /** Best effort: a failed snapshot is logged and never blocks the user action. */
  async take(survey: Survey, reason: SurveySnapshotReason): Promise<boolean> {
    try {
      const counts = countSurvey(survey);
      await this.db.surveySnapshots.add({
        surveyId: survey.id,
        createdAt: Date.now(),
        reason,
        sheetCount: counts.sheets,
        locationCount: counts.locations,
        survey: structuredClone(survey),
      });
      await this.pruneOldSnapshots(survey.id);
      this.surveyLog.success('survey.snapshot', { surveyId: survey.id, reason, after: counts });
      return true;
    } catch (error: unknown) {
      this.surveyLog.failure('survey.snapshot', error, { surveyId: survey.id, reason, source: 'idb' });
      return false;
    }
  }

  async list(surveyId: string): Promise<SurveySnapshot[]> {
    try {
      const snapshots = await this.db.surveySnapshots.where('surveyId').equals(surveyId).toArray();
      return snapshots.sort((a, b) => b.createdAt - a.createdAt || (b.id ?? 0) - (a.id ?? 0));
    } catch (error: unknown) {
      this.surveyLog.failure('survey.snapshot', error, { surveyId, source: 'idb' });
      return [];
    }
  }

  async get(snapshotId: number): Promise<SurveySnapshot | undefined> {
    return this.db.surveySnapshots.get(snapshotId);
  }

  async remove(surveyId: string): Promise<void> {
    await this.db.surveySnapshots.where('surveyId').equals(surveyId).delete();
  }

  /** Works on primary keys only, so pruning never loads the stored survey copies into memory. Ids grow with time. */
  private async pruneOldSnapshots(surveyId: string): Promise<void> {
    const snapshotIds = await this.db.surveySnapshots.where('surveyId').equals(surveyId).primaryKeys();
    const staleIds = snapshotIds.sort((a, b) => b - a).slice(MAX_SNAPSHOTS_PER_SURVEY);

    if (staleIds.length > 0) {
      await this.db.surveySnapshots.bulkDelete(staleIds);
    }
  }
}
